import { computed } from "vue";
import { useTheme } from "vuetify";

/**
 * Dark ⇄ light for the shell chrome AND the hosted module: the global state
 * is the single source of truth (it flips `body[data-theme]`, which some
 * modules key off directly), Vuetify follows it. Shared by SidebarFooter
 * (signed in) and PreAuthPage (before sign-in) — the tenant's default theme
 * sticks until the visitor chooses, from the first screen onwards.
 */
export function useThemeToggle() {
  const theme = useTheme();
  const darkMode = computed(() => theme.global.current.value.dark);

  function toggleTheme(): void {
    window.PinksyGlobalState?.toggleTheme();
    const isDark = window.PinksyGlobalState?.getState().darkMode ?? !darkMode.value;
    theme.change(isDark ? "dark" : "light");
  }

  /**
   * 🔴 Ask for a theme BY NAME, rather than for the other one.
   *
   * The user menu offers Light and Dark as two pressed-state controls (D52), and a control that says
   * which one you are on has to be idempotent: pressing "Dark" while already dark must do nothing.
   * Wired to `toggleTheme` it would flip to light — the exact bug an icon-only toggle hides, because
   * an icon never claims to be a state in the first place.
   */
  function setDarkMode(next: boolean): void {
    if (darkMode.value === next) return;
    toggleTheme();
  }

  return { darkMode, toggleTheme, setDarkMode };
}
